import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useQuery } from '@tanstack/react-query';
import { router, useLocalSearchParams } from 'expo-router';
import { useAtom, useAtomValue } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { useEffect, useState } from 'react';

import { useAnnouncementHeaderBanner } from '@/components/AnnouncementHeaderBanner';
import { AwayBanner } from '@/components/awayBanner';
import { Header } from '@/components/header';
import { WebPageHeading } from '@/components/WebPageHeading';
import { DesktopInbox } from '@/components/inbox/DesktopInbox';
import { MobileInbox } from '@/components/inbox/MobileInbox';
import { TAB_ROUTES } from '@/components/inbox/constants';
import { TAB_HEADER_ITEMS } from '@/components/tabHeaderItems';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { useGroupedInquiriesEnabled } from '@/hooks/inbox/useGroupedInquiriesEnabled';
import { useResetInboxOnFocus } from '@/hooks/inbox/useResetInboxOnFocus';
import { useIsBelowBreakpoint } from '@/hooks/useResponsiveLayout';
import { useUnreadCounts } from '@/hooks/useUnreadCounts';
import { getInquiryCancerTypesQueryOptions } from '@/services/getInquiryCancerTypes';
import { selectedCancerFocusAtom, tabSelectedKeyAtom, userIdAtom } from '@/services/globalState';
import { segmentClient } from '@/util/analytics';
import { type InboxTabContext } from '@/util/analyticsEvents';
import { shouldFetchUserScoped } from '@/util/authTokens';
import { getCancerFocusId } from '@/util/getCancerFocusId';

const OPEN_TAB_INDEX = TAB_ROUTES.findIndex((route) => route.key === 'open');

export default function Inbox() {
  const params = useLocalSearchParams<{
    id?: string;
  }>();

  const resetSelectedChatId = () => {
    router.setParams({ id: undefined });
  };

  const announcementBanner = useAnnouncementHeaderBanner();

  const resetSelectedCancerFocus = useResetAtom(selectedCancerFocusAtom);

  const [selectedCancerFocus, setSelectedCancerFocus] = useAtom(selectedCancerFocusAtom);

  const selectedChatId = params.id === 'undefined' ? undefined : params.id;

  const [tabSelected, setTabSelected] = useAtom(tabSelectedKeyAtom);

  const [tabRoutes, setTabRoutes] = useState(TAB_ROUTES);

  const userId = useAtomValue(userIdAtom);

  /** Inbox uses md (768px): DesktopInbox (list+chat split) at 768px+, MobileInbox below. */
  const isCompactInboxLayout = useIsBelowBreakpoint('md');

  const isDesktopWeb = isWeb && !isCompactInboxLayout;

  const isInboxGroupedInquiriesEnabled = useGroupedInquiriesEnabled();

  const inquiryCancerTypesQuery = useQuery({
    ...getInquiryCancerTypesQueryOptions(userId),
    enabled: shouldFetchUserScoped(userId),
  });

  const selectedCancerFocusId = getCancerFocusId(selectedCancerFocus, inquiryCancerTypesQuery.data);

  const { unreadByTab } = useUnreadCounts(selectedCancerFocusId);

  useResetInboxOnFocus({
    selectedChatId,
    onReset: () => {
      resetSelectedChatId();

      resetSelectedCancerFocus();
    },
  });

  useEffect(() => {
    const event = {
      all: ANALYTICS_EVENTS.INBOX_VIEWED,
      open: ANALYTICS_EVENTS.INBOX_VIEWED,
      drafts: ANALYTICS_EVENTS.DRAFTS_VIEWED,
      closed: ANALYTICS_EVENTS.CLOSED_INBOX_VIEWED,
    }[tabSelected];

    if (event) {
      Promise.resolve(
        segmentClient.track(event, {
          inquiryGroupsEnabled: isInboxGroupedInquiriesEnabled,
          inboxTab: tabSelected as InboxTabContext,
        })
      ).catch(() => undefined);
    }
  }, [isInboxGroupedInquiriesEnabled, tabSelected]);

  useEffect(() => {
    setTabRoutes((prev) => {
      const newRoutes = prev.map((route) => {
        const nextCount = unreadByTab[route.key as keyof typeof unreadByTab] ?? 0;

        if (route.count === nextCount) {
          return route;
        }

        return { ...route, count: nextCount };
      });

      const didChange = newRoutes.some((route, idx) => route !== prev[idx]);

      if (!didChange) {
        return prev;
      }

      return newRoutes;
    });
  }, [unreadByTab]);

  const onIndexChange = (index: number) => {
    const selectedTab = TAB_ROUTES[index].key;

    setTabSelected(selectedTab);
  };

  const selectedTabIndex = TAB_ROUTES.findIndex((route) => route.key === tabSelected);

  const tabState = {
    index: selectedTabIndex >= 0 ? selectedTabIndex : OPEN_TAB_INDEX,
    routes: tabRoutes,
  };

  return (
    <VStack className="w-full flex-1">
      <VStack className="w-full flex-1">
        <Header
          headerShown={!isWeb}
          title="Inbox"
          isLarge
          contentScrollsUnderHeader
          rightItems={TAB_HEADER_ITEMS}
        />

        <WebPageHeading>Inbox</WebPageHeading>

        {isDesktopWeb ? (
          <>
            <AwayBanner userId={userId} linkToSettings />

            <DesktopInbox
              userId={userId}
              selectedChatId={selectedChatId}
              selectedCancerFocus={selectedCancerFocus}
              selectedCancerFocusId={selectedCancerFocusId}
              inquiryCancerTypesQuery={inquiryCancerTypesQuery}
              tabSelected={tabSelected}
              tabState={tabState}
              unreadByTab={unreadByTab}
              onIndexChange={onIndexChange}
              setTabSelected={setTabSelected}
              setSelectedCancerFocus={setSelectedCancerFocus}
              resetSelectedChatId={resetSelectedChatId}
              resetSelectedCancerFocus={resetSelectedCancerFocus}
            />
          </>
        ) : (
          <MobileInbox
            leadBanners={
              <>
                {announcementBanner}

                <AwayBanner userId={userId} linkToSettings />
              </>
            }
            selectedCancerFocus={selectedCancerFocus}
            inquiryCancerTypesQuery={inquiryCancerTypesQuery}
            tabState={tabState}
            onIndexChange={onIndexChange}
            setSelectedCancerFocus={setSelectedCancerFocus}
            resetSelectedChatId={resetSelectedChatId}
          />
        )}
      </VStack>
    </VStack>
  );
}
